<template>
  <el-dialog
    v-model="visible"
    title="订单发货"
    width="500px"
    :before-close="handleClose"
    :close-on-click-modal="false"
  >
    <el-form
      ref="formRef"
      :model="formData"
      :rules="rules"
      label-width="100px"
      @submit.prevent
    >
      <!-- 订单信息 -->
      <el-card class="order-info" shadow="never">
        <template #header>
          <span>订单信息</span>
        </template>
        <div class="info-row">
          <span class="label">订单编号：</span>
          <span class="value">{{ orderData.id || '无' }}</span>
        </div>
        <div class="info-row">
          <span class="label">收货人：</span>
          <span class="value">{{ orderData.postinfo?.name || '无' }}</span>
        </div>
        <div class="info-row">
          <span class="label">联系电话：</span>
          <span class="value">{{ orderData.postinfo?.phone || '无' }}</span>
        </div>
        <div class="info-row">
          <span class="label">收货地址：</span>
          <span class="value">{{ orderData.postinfo?.address || '无' }}</span>
        </div>
      </el-card>

      <!-- 发货信息 -->
      <el-form-item label="快递单号" prop="postcode" class="form-item">
        <el-input
          v-model="formData.postcode"
          placeholder="请输入快递单号"
          clearable
          maxlength="50"
          show-word-limit
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          确认发货
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ref, watch, reactive } from 'vue';
import { ElMessage, type FormInstance, type FormRules } from 'element-plus';
import { useApi } from '@/hooks/useApi';

const props = defineProps<{
  modelValue: boolean;
  orderData: any;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'success': [];
}>();

const visible = ref(false);
const formRef = ref<FormInstance>();
const submitting = ref(false);

// 表单数据
const formData = reactive({
  postcode: '',
});

// 表单验证规则
const rules: FormRules = {
  postcode: [
    { required: true, message: '请输入快递单号', trigger: 'blur' },
    { min: 5, max: 50, message: '快递单号长度应在5-50个字符之间', trigger: 'blur' },
  ],
};

// 重置表单
const resetForm = () => {
  formData.postcode = '';
  formRef.value?.clearValidate();
};

// 监听props变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal;
  if (newVal) {
    // 重置表单
    resetForm();
  }
}, { immediate: true });

// 监听visible变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal);
});

// 关闭弹窗
const handleClose = () => {
  visible.value = false;
};

// 提交发货
const handleSubmit = async() => {
  if (!formRef.value) {
    return;
  }

  try {
    const valid = await formRef.value.validate();
    if (!valid) {
      return;
    }

    submitting.value = true;

    await useApi().billApi.shipBillbill({
      billId: props.orderData.id,
      postcode: formData.postcode,
    });

    ElMessage.success('发货成功');
    emit('success');
    handleClose();
  } catch (error) {
    ElMessage.error('发货失败，请重试');
    console.error('发货失败:', error);
  } finally {
    submitting.value = false;
  }
};
</script>

<style lang="scss" scoped>
.order-info {
  margin-bottom: 20px;

  .info-row {
    display: flex;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      width: 80px;
      color: #606266;
      font-weight: 500;
    }

    .value {
      flex: 1;
      color: #303133;
    }
  }
}

.form-item {
  margin-bottom: 20px;
}

.dialog-footer {
  text-align: right;
}
</style>
